import { useState, useRef, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'
import { MdClose } from 'react-icons/md'
import { Typography } from 'components/common/typography'
import { useLesson, useLessons } from 'querys/lesson'
import { useBadge, useCourse } from 'querys/course'
import CircularProgress from 'common/circular-progress'
import { ArrowLeft } from 'icons/arrow'
import { TransparentButton } from 'common/button'
import {
  LessonsFullPathStyled,
  LessonFullPathStyled,
} from './lessons-full-path-styles'

function LessonFullPath({ title, slug, path, numberLesson, video, completed, setScroll }) {
  const { lesson_slug } = useRouter().query
  let progress = 0
  if (video) {
    progress = Number((video.progress / video.duration).toFixed(2))
  }
  const active = slug === lesson_slug
  const lessonRef = useRef(null)
  useEffect(() => {
    if (!active || !window.matchMedia('(min-width: 1024px)').matches) return
    // Here we scroll the bar to this lesson position
    setScroll(lessonRef.current.offsetTop)
  }, [lesson_slug])
  return (
    <Link href={`${path}/${slug}`}>
      <LessonFullPathStyled
        className={active ? 'lesson-full-path lesson-active' : 'lesson-full-path'}
        ref={lessonRef}
        data-cy={slug}
      >
        <div className="lesson-dot-container">
          <div className="lesson-dot">
            <CircularProgress
              diameter={24}
              thickness={6}
              multiplier={13.5}
              progress={completed ? 1 : progress}
            />
          </div>
          <span>{numberLesson}</span>
        </div>
        <div className="lesson-title-container">
          <Typography as="h4" small="regular">{title}</Typography>
        </div>
      </LessonFullPathStyled>
    </Link>
  )
}

function ChapterFullPath({ title, lessons, path, initalLesson, setScroll }) {
  return (
    <div className="chapter-path">
      <div className="chapter-title">
        <div className="title-dot-container">
          <div className="title-dot" />
        </div>
        <Typography className="chapter-title" as="h3" small="regularBold">{title}</Typography>
      </div>
      {lessons?.map((lesson, idx) => (
        <LessonFullPath
          key={lesson.uuid}
          setScroll={setScroll}
          numberLesson={idx + initalLesson + 1}
          path={path}
          {...lesson}
        />
      ))}
    </div>
  )
}

function LessonsFullPath({ setLessonsOpen, show }) {
  const { data: chapters } = useLessons()
  const { data: {
    course: { title: courseTitle, slug: courseSlug },
  }} = useLesson()
  const { data } = useBadge()
  const badge = data?.badge
  const [scroll, setScroll] = useState(0)
  const lessonList = useRef(null)
  const openAndSaveFullBar = () => {
    setLessonsOpen(!show)
    window.localStorage.setItem('lessons-path', !show)
  }
  useEffect(() => {
    if (scroll > 180 && window.matchMedia('(min-width: 1024px)').matches) {
      // IF THE BAR IS CLOSED
      if (document.querySelector('.desktop-hidden')) {
        lessonList.current.scrollTo(0, scroll - 80 - 56 - 72)
        return
      }
      // IF THE BAR IS OPEN
      lessonList.current.scrollTo(0, scroll - 160 - 81 - 72)
    }
  }, [scroll])

  return (
    <LessonsFullPathStyled show={show} className={!show && 'desktop-hidden'}>
      <div className="full-path-wrapper">
        <div className="show-path" onClick={() => openAndSaveFullBar()}>
          <div className="arrow-container">
            <ArrowLeft color="var(--white)" />
          </div>
          <p className="show-path-text">Ocultar Contenido</p>
        </div>
        <div className="title-full-path" >
          <Link href={`/aprender/${courseSlug}`} passHref>
            <img width="30" height="30" src={badge?.image} alt={badge?.description} />
            <Typography as="h2" color="var(--baseNeutral)" small="regularBold" align="left">{courseTitle}</Typography>
          </Link>
          <TransparentButton onClick={() => setLessonsOpen(false)}>
            <MdClose color="var(--secondary)" size="24" />
          </TransparentButton>
        </div>
        <div className="lessons-list" ref={lessonList}>
          {chapters?.map((chapter, idx) => (
            <ChapterFullPath
              {...chapter}
              key={chapter.uuid}
              setScroll={setScroll}
              path={`/aprender/${courseSlug}`}
              initalLesson={chapters.map((chap, chapIdx) => (chapIdx < idx ? chap.lessons.length : 0)).reduce((accumulator, currentValue) => accumulator + currentValue)}
            />
          ))}
        </div>
      </div>
    </LessonsFullPathStyled>
  )
}

export default LessonsFullPath
